import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Star, ChevronRight, Trash2, Flag, Lock, Unlock } from "lucide-react";
import { factionIcon } from "@/lib/game";
import { api } from "@/lib/api";

const slug = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 24);
// Deterministic opponent id so the same opponent always yields the same matrix keys
// (keeps the matrix sticky across devices / re-selection / scouts).
const oppId = (p) => `opp_${p.ls_id ? `ls${p.ls_id}` : `${slug(p.name)}_${slug(p.faction)}`}`;

export default function OpponentPicker({ setup, updateSetup }) {
  const oppKey = setup.you_are === "A" ? "B" : "A";
  const locked = !!setup.opponent_locked;
  const [events, setEvents] = useState([]);
  const [scouts, setScouts] = useState([]);
  const [eventId, setEventId] = useState("");
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadScouts = () => api.scouts().then(setScouts).catch(() => {});
  useEffect(() => { api.events().then(setEvents).catch(() => {}); loadScouts(); }, []);

  const loadEvent = async (id) => {
    setEventId(id); setBusy(true);
    try { const full = await api.getEvent(id); setTeams(full.teams || []); }
    catch { toast.error("Could not load event"); }
    finally { setBusy(false); }
  };

  const toggleLock = () => updateSetup({ ...setup, opponent_locked: !locked });

  const setOpponent = (team, eid) => {
    if (locked) return toast.error("Opponent is locked — unlock to change it");
    const players = (team.players || []).map((p) => ({
      id: oppId(p), name: p.name, faction: p.faction, team: oppKey,
      lists: (p.lists || []).map((l) => ({ name: l.name, army: p.faction, text: l.text })),
      ls_id: p.ls_id || null, ls_event_id: eid || p.ls_event_id || null,
    }));
    const others = setup.players.filter((p) => p.team !== oppKey);
    // Keep existing cells (sticky per opponent) and merge any matrix saved with a scout.
    const matrix = { ...(setup.matrix || {}), ...(team.matrix || {}) };
    updateSetup({ ...setup, [oppKey === "A" ? "team_a_name" : "team_b_name"]: team.name, players: [...others, ...players], matrix });
    toast.success(`${team.name} set as your opponent`);
  };

  const saveCurrentScout = async () => {
    const opp = setup.players.filter((p) => p.team === oppKey);
    if (!opp.length) return toast.error("No opponent loaded to save");
    const name = (oppKey === "A" ? setup.team_a_name : setup.team_b_name) || "Opponent";
    const oppIds = new Set(opp.map((p) => p.id));
    const matrix = {};
    Object.entries(setup.matrix || {}).forEach(([k, v]) => { if (oppIds.has(k.split("|")[1])) matrix[k] = v; });
    await api.saveScout({
      event_name: setup.event_name || "", name, country: "",
      players: opp.map((p) => ({ name: p.name, faction: p.faction, lists: p.lists, ls_id: p.ls_id, ls_event_id: p.ls_event_id })),
      matrix,
    });
    await loadScouts();
    toast.success(`Saved scout: ${name} (with matrix)`);
  };
  const delScout = async (id) => { await api.delScout(id); setScouts((s) => s.filter((x) => x.id !== id)); };

  return (
    <Card className="bg-[#121824]/70 border-cyan-700/30 p-4 sm:p-5" data-testid="opponent-picker-card">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2"><Users className="h-4 w-4 text-cyan-400" /> Opponent</h3>
        <div className="flex items-center gap-2">
          <Button data-testid="lock-opponent-button" onClick={toggleLock} size="sm" variant="outline"
            className={`h-7 text-xs ${locked ? "border-amber-500/60 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800"}`}>
            {locked ? <><Lock className="h-3.5 w-3.5 mr-1" /> Locked</> : <><Unlock className="h-3.5 w-3.5 mr-1" /> Lock</>}
          </Button>
          <Button data-testid="save-opponent-scout" onClick={saveCurrentScout} disabled={!setup.players.some((p) => p.team === oppKey)} size="sm" variant="outline" className="h-7 border-slate-700 bg-transparent text-amber-300 hover:bg-amber-950/30 text-xs disabled:opacity-40"><Star className="h-3.5 w-3.5 mr-1" /> Save as scout</Button>
        </div>
      </div>
      {locked ? (
        <p className="text-xs text-amber-300/90 mb-3 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Opponent locked so the team can fill the matrix without it changing. Unlock to switch opponent.</p>
      ) : (
        <p className="text-xs text-slate-500 mb-3">Pick this round's opponent from a saved event (import events in <b>Event Import</b>) or reuse a saved scout. Ratings are remembered per opponent.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={eventId} onValueChange={loadEvent} disabled={locked}>
          <SelectTrigger data-testid="opp-event-select" className="bg-slate-950/60 border-slate-700 h-9"><SelectValue placeholder="Saved event" /></SelectTrigger>
          <SelectContent>
            {events.length === 0 ? <SelectItem value="none" disabled>No saved events</SelectItem>
              : events.map((e) => (<SelectItem key={e.id} value={e.id}>{e.event_name || e.id} ({e.team_count ?? 0})</SelectItem>))}
          </SelectContent>
        </Select>
        <Select disabled={!teams.length || locked} onValueChange={(i) => setOpponent(teams[+i], eventId)}>
          <SelectTrigger data-testid="opp-team-select" className="bg-slate-950/60 border-cyan-700/40 h-9"><SelectValue placeholder={busy ? "Loading…" : "Opponent team"} /></SelectTrigger>
          <SelectContent className="max-h-72">
            {teams.map((t, i) => (<SelectItem key={`${t.name}-${i}`} value={String(i)}>{t.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {scouts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-800">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-amber-400" /> Saved scouts</p>
          <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
            {scouts.map((s) => (
              <div key={s.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 flex items-center gap-3" data-testid="scout-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-100 truncate">{s.name}</p>
                    {s.matrix && Object.keys(s.matrix).length > 0 && <span className="text-[10px] text-emerald-300/90 flex items-center gap-0.5" data-testid="scout-has-matrix">+matrix</span>}
                    {s.country && <span className="text-[11px] text-slate-500 font-mono flex items-center gap-1"><Flag className="h-3 w-3" />{s.country}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    {(s.players || []).map((p, j) => (
                      <span key={`${p.name}-${j}`} className="text-[11px] text-slate-400 flex items-center gap-1">
                        <img src={factionIcon(p.faction)} alt="" className="h-3.5 w-3.5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
                <Button data-testid="scout-set-opponent" onClick={() => setOpponent(s, s.players?.[0]?.ls_event_id)} disabled={locked} size="sm" className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold shrink-0 h-7 text-xs disabled:opacity-40">Use <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button>
                <Button data-testid="scout-delete" onClick={() => delScout(s.id)} variant="ghost" size="icon" className="text-slate-500 hover:text-red-400 shrink-0 h-7 w-7"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
